import React from 'react';
import { LABEL_CLASS } from 'components/crm/ui/fieldStyles';
import { Check } from 'lucide-react';
import {
  APPOINTMENT_COLORS,
  DEFAULT_APPOINTMENT_COLOR_ID,
  appointmentColorName,
  appointmentColorOf,
} from 'constants/appointmentColors';
import { cx } from 'components/crm/ui/uiUtils';
import useText from 'hooks/useText';

/**
 * The color of an appointment: one of the 11 of Google Calendar (`value` its
 * colorId) or '' for the color of its category. Native radios: Tab reaches
 * the group, the arrows move the choice.
 */
const ColorField = ({ id = 'appointment-color', value = '', onChange }) => {
  const { text, language } = useText('appointmentFields');
  // The Language manager keeps the current map only: French is its map.
  const languageCode =
    language?.current && language.current === language.languages?.get('fr')
      ? 'fr'
      : 'en';
  const options = [
    { id: DEFAULT_APPOINTMENT_COLOR_ID, name: text('colorDefault'), hex: null },
    ...APPOINTMENT_COLORS.map((color) => ({
      id: color.id,
      name: appointmentColorName(color, languageCode),
      hex: color.hex,
    })),
  ];
  const selectedName =
    appointmentColorName(appointmentColorOf(value), languageCode) ||
    text('colorDefault');

  return (
    <fieldset className="flex flex-col gap-2">
      <legend className={`mb-1.5 ${LABEL_CLASS}`}>
        {text('colorLabel')}
        <span className="ml-2 text-xs font-normal text-faint">
          {selectedName}
        </span>
      </legend>
      <div className="flex flex-wrap items-center gap-2">
        {options.map((option) => {
          const checked = option.id === (value ?? '');
          return (
            <label
              key={option.id || 'default'}
              title={option.name}
              className="cursor-pointer"
            >
              <input
                type="radio"
                name={id}
                value={option.id}
                checked={checked}
                aria-label={option.name}
                onChange={() => onChange(option.id)}
                className="peer sr-only"
              />
              <span
                className={cx(
                  'flex size-7 items-center justify-center rounded-full border-2 transition-shadow peer-focus-visible:ring-2 peer-focus-visible:ring-primary peer-focus-visible:ring-offset-2',
                  option.hex
                    ? 'border-transparent text-white'
                    : 'border-lineStrong bg-white text-ink',
                  checked && 'ring-2 ring-ink ring-offset-2',
                )}
                style={option.hex ? { backgroundColor: option.hex } : undefined}
              >
                {checked && <Check size={14} strokeWidth={3} />}
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
};

export default ColorField;
